import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ChangeEvent } from '@ckeditor/ckeditor5-angular';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { DialogDocumentsAddSingleConfirm } from 'app/dialogs/documents/documents-add-single-confirm/dialog-documents-add-single-confirm';
import { base64ToArrayBuffer, saveByteArray } from 'app/helpers/blobDownload';
import { formatKennitala } from 'app/helpers/formatKennitala';
import { formatPersonDetails } from 'app/helpers/formatPersonDetails';
import { subtractYears } from 'app/helpers/subtractYears';
import { DocumentsDivisionDefaultValues } from 'app/interfaces/documents/documentsDivisionDefaultValues';
import { PensionSummaryUploadMessageTemplateData } from 'app/interfaces/documents/pensionSummaryUploadMessageTemplateData';
import { DropDownItem } from 'app/interfaces/items/dropDownItem';
import { ExposedListPublicRegistryCompaniesRes } from 'app/interfaces/responses/Exposed/exposedListPublicRegistryCompaniesRes';
import { ExposedListPublicRegistryPersonsRes } from 'app/interfaces/responses/Exposed/exposedListPublicRegistryPersonsRes';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { SkjalamidjaService } from 'app/services/api/skjalamidja/skjalamidja.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { DOCUMENT_TYPES } from 'common/constants/document-type.constants';
import { USER_ROLES } from 'common/constants/user-role.constants';
import moment from 'moment';
import { Subscription } from 'rxjs';
import {
    DocumentsAddGeneralInfoComponent,
    DocumentsAddGeneralInfoValue,
} from '../documents-add-general-info/documents-add-general-info.component';

@Component({
    selector: 'app-documents-add-single',
    templateUrl: './documents-add-single.component.html',
    styleUrls: ['./documents-add-single.component.scss'],
})
export class DocumentsAddSingleComponent implements OnInit, OnDestroy {
    //#region Globals
    @ViewChild(DocumentsAddGeneralInfoComponent)
    generalInfoComponent: DocumentsAddGeneralInfoComponent;

    userRoles = USER_ROLES;
    todayDate: Date = new Date();
    form: UntypedFormGroup;
    formEnabled = false;
    submitted = false;
    files: File[] = [];
    descriptionSingleDocTextHTML: string;
    selectedSingleDocRecipient: ExposedListPublicRegistryPersonsRes | ExposedListPublicRegistryCompaniesRes;

    categoriesDropDown: Array<DropDownItem> = [];
    subcategoriesDropDown: Array<DropDownItem> = [];
    systemsDropDown: Array<DropDownItem> = [];
    typesDropDown: Array<DropDownItem> = [];
    svidDropDown: Array<DropDownItem> = [];
    deildirDropDown: Array<DropDownItem> = [];
    documentTemplatesDropDown: Array<DropDownItem> = [];
    descriptionSingleDocEditor = ClassicEditor;
    svid: string[] = [];
    deildir: string[] = [];
    pdfFiles = true;
    documentPensionTemplateReady = false;
    userDetails: ExposedReadUserDetailsRes = null;
    localStorageDivisionDefaultValues: DocumentsDivisionDefaultValues;
    showAdditionalInputs: boolean = false;
    // Stores all observable subscriptions
    subscriptions: Subscription[] = [];

    //#endregion

    //#region Init

    constructor(
        private route: ActivatedRoute,
        private lsrauthService: LsrauthService,
        private _formBuilder: UntypedFormBuilder,
        private skjalamidja: SkjalamidjaService,
        private alertsService: AlertsService,
        private router: Router,
        private dialog: MatDialog,
        private translationService: TranslationService
    ) {
        this.lsrauthService.userDetails.subscribe((userDetails) => (this.userDetails = userDetails));
    }

    ngOnInit(): void {
        this.initDropdowns();
        this.initForm();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    initDropdowns() {
        var [categoriesRes, subcategoriesRes, systemsRes, typesRes, departmentsRes, documentTemplates] =
            this.route.snapshot.parent.data.dropdownData;

        documentTemplates.forEach((x) => {
            this.documentTemplatesDropDown.push({
                value: x.templateKey.toString(),
                viewValue: x.templateTranslationKey,
            });
        });
    }

    initForm(): void {
        this.submitted = false;
        this.descriptionSingleDocTextHTML = '';
        this.descriptionSingleDocEditor = ClassicEditor;
        this.form = this._formBuilder.group({
            generalInfo: new FormControl(''),
            ssn: new UntypedFormControl(null, [Validators.required]),
            documentType: new UntypedFormControl('file', [Validators.required]),
            documentTemplateKey: new UntypedFormControl(''),
            documentTemplatePaymentFromDate: new UntypedFormControl(subtractYears(new Date(), 1)),
            documentTemplatePaymentToDate: new UntypedFormControl(new Date()),
            documentTemplateSalaryFromDate: new UntypedFormControl(''),
            documentTemplateSalaryToDate: new UntypedFormControl(''),
            documentTemplateLetterMail: new UntypedFormControl(false),
            includeAdditionalDocument: new FormControl(false),
        });
        this.formEnabled = true;

        const formSubscription = this.form.valueChanges.subscribe((value) => {
            this.checkPensionTemplateDocumentReady();
        });
        this.subscriptions.push(formSubscription);

        // Additional input fields should only be shown for pension overview document types
        const templateKeySubscription = this.form.get('documentTemplateKey').valueChanges.subscribe((value) => {
            const types = [
                DOCUMENT_TYPES.DOC_TYPE_PENSION_A_DEILD,
                DOCUMENT_TYPES.DOC_TYPE_PENSION_B_DEILD,
                DOCUMENT_TYPES.DOC_TYPE_PENSION_RECIPIENT,
                DOCUMENT_TYPES.DOC_TYPE_PENSION_SEREIGN,
            ];
            this.showAdditionalInputs = types.includes(value);
        });
        this.subscriptions.push(templateKeySubscription);
    }

    //#endregion

    //#region Document

    onSelect(event: any): void {
        this.files.push(...event.addedFiles);

        // Keep only one file at a time
        if (this.files.length > 1) {
            this.files.splice(0, 1);
        }

        // If filename is short enough and the document type is 'file', set it as the default title
        if (this.files[0].name.length <= 255 && this.form.get('documentType')?.value === 'file') {
            this.form.patchValue({
                generalInfo: {
                    ...this.form.get('generalInfo')?.value,
                    title: this.files[0].name,
                },
            });
        }

        this.form.markAsDirty();

        this.prepareAttachments();
    }

    onRemove(event: File): void {
        // Remove selected file
        this.files.splice(this.files.indexOf(event), 1);
        this.prepareAttachments();
    }

    prepareAttachments(): void {
        this.checkIfPdfFiles();
    }

    checkIfPdfFiles(): void {
        // Reset PDF-only flag and form value
        this.form.patchValue({
            generalInfo: {
                ...this.form.get('generalInfo')?.value,
                submitToStafraentIsland: false,
            },
        });

        this.pdfFiles = true;

        // Set to false if any file is not a PDF
        this.files.forEach((file) => {
            if (file.type !== 'application/pdf') {
                this.pdfFiles = false;
            }
        });
    }

    //#endregion

    //#region HTTP

    confirmAndSave() {
        let templateName = null;
        if (this.form.get('documentTemplateKey').value) {
            templateName = this.translationService.translate(
                this.documentTemplatesDropDown.find((x) => x.value == this.form.get('documentTemplateKey').value).viewValue
            );
        }

        const dialogRef = this.dialog.open(DialogDocumentsAddSingleConfirm, {
            autoFocus: false,
            restoreFocus: false,
            width: '600px',
            data: {
                ssn: this.form.get('ssn').value,
                name: this.selectedSingleDocRecipient.name,
                fileName: this.files[0]?.name ?? null,
                templateName: templateName,
                documentType: this.form.get('documentType').value,
            },
        });
        dialogRef.afterClosed().subscribe((confirmed) => {
            if (confirmed) {
                this.save();
            }
        });
    }

    save() {
        this.generalInfoComponent.form.markAllAsTouched();
        this.submitted = true;
        if (
            (this.form.get('documentType').value == 'file' && (!this.form.valid || this.files.length == 0)) ||
            (this.form.get('documentType').value == 'template' && !this.form.valid)
        ) {
            this.alertsService.changealertHeader('Villur í formi');
            this.alertsService.changeAlertErrorMessage('Vinsamlegast leysið úr villum í formi');
            this.alertsService.changeAlertErrorShow(true);
            return;
        }
        this.formEnabled = false;
        this.storeSelectedValues();

        if (this.form.get('documentType').value == 'template') {
            this.saveUploadTemplate();
        } else {
            this.saveUpload();
        }
    }

    getUploadedDocumentFromTemplatePreview() {
        var generalInfo: DocumentsAddGeneralInfoValue = this.form.get('generalInfo').value;

        this.skjalamidja
            .getUploadedDocumentFromTemplatePreview(
                {
                    ssn: this.form.get('ssn').value,
                    documentTemplateKey: this.form.get('documentTemplateKey').value,
                    title: generalInfo.title,
                    description: generalInfo.description,
                    documentDate: generalInfo.documentDate,
                    documentTypeId: generalInfo.documentTypeId,
                    documentCategoryId: generalInfo.documentCategoryId,
                    documentSubcategoryId: generalInfo.documentSubcategoryId,
                    documentSystemId: generalInfo.documentSystemId,
                    svid: generalInfo.svid,
                    deild: generalInfo.deild,
                    publicationDate: generalInfo.publicationDate,
                    submitToStafraentIsland: generalInfo.submitToStafraentIsland,
                    submitToPortalLSR: generalInfo.submitToPortalLSR,
                    templateData: this.getTemplateData() != null ? JSON.stringify(this.getTemplateData()) : null,
                },
                this.files
            )
            .subscribe((fileRes) => {
                var sampleArr = base64ToArrayBuffer(fileRes.base64File);
                saveByteArray('skjal_' + this.form.get('ssn').value + '.pdf', sampleArr);
            });
    }

    saveUploadTemplate() {
        var generalInfo: DocumentsAddGeneralInfoValue = this.form.get('generalInfo').value;

        this.skjalamidja
            .uploadDocumentFromTemplate(
                {
                    ssn: this.form.get('ssn').value,
                    documentTemplateKey: this.form.get('documentTemplateKey').value,
                    title: generalInfo.title,
                    description: generalInfo.description,
                    documentDate: generalInfo.documentDate,
                    documentTypeId: generalInfo.documentTypeId,
                    documentCategoryId: generalInfo.documentCategoryId,
                    documentSubcategoryId: generalInfo.documentSubcategoryId,
                    documentSystemId: generalInfo.documentSystemId,
                    svid: generalInfo.svid,
                    deild: generalInfo.deild,
                    publicationDate: generalInfo.publicationDate,
                    submitToStafraentIsland: generalInfo.submitToStafraentIsland,
                    submitToPortalLSR: generalInfo.submitToPortalLSR,
                    templateData: this.getTemplateData() != null ? JSON.stringify(this.getTemplateData()) : null,
                },
                this.files
            )
            .subscribe((res) => {
                this.formEnabled = true;
                if (res.createDocSuccess) {
                    this.router.navigate(['/skjalasafn/skoda-skjol']);
                    if (generalInfo.submitToStafraentIsland && !res.stafraentIslandSuccess) {
                        this.alertsService.changealertHeader('Villa kom upp');
                        this.alertsService.changeAlertErrorMessage('Ekki tókst að senda skjal í pósthólf Stafræns Íslands');
                        this.alertsService.changeAlertErrorShow(true);
                    }
                }
            });
    }

    // Get the template data for the given document template type
    getTemplateData() {
        switch (this.form.get('documentTemplateKey').value) {
            case DOCUMENT_TYPES.DOC_TYPE_PENSION_A_DEILD:
            case DOCUMENT_TYPES.DOC_TYPE_PENSION_B_DEILD:
            case DOCUMENT_TYPES.DOC_TYPE_PENSION_RECIPIENT:
            case DOCUMENT_TYPES.DOC_TYPE_PENSION_SEREIGN:
                return {
                    letterMail: this.form.get('documentTemplateLetterMail').value,
                    paymentFromDate: moment(this.form.get('documentTemplatePaymentFromDate').value).format('YYYY-MM-DD'),
                    paymentToDate: moment(this.form.get('documentTemplatePaymentToDate').value).format('YYYY-MM-DD'),
                    salaryFromDate: this.form.get('documentTemplateSalaryFromDate').value
                        ? moment(this.form.get('documentTemplateSalaryFromDate').value).format('YYYY-MM-DD')
                        : null,
                    salaryToDate: this.form.get('documentTemplateSalaryToDate').value
                        ? moment(this.form.get('documentTemplateSalaryToDate').value).format('YYYY-MM-DD')
                        : null,
                    showSalaryDate: !!(
                        this.form.get('documentTemplateSalaryToDate').value && this.form.get('documentTemplateSalaryFromDate').value
                    ),
                } as PensionSummaryUploadMessageTemplateData;
            default:
                return null;
        }
    }

    saveUpload() {
        var generalInfo: DocumentsAddGeneralInfoValue = this.form.get('generalInfo').value;
        this.skjalamidja
            .uploadDocument(
                {
                    azureLocation: null,
                    description: generalInfo.description,
                    documentCategoryId: generalInfo.documentCategoryId,
                    documentDate: generalInfo.documentDate,
                    documentSubcategoryId: generalInfo.documentSubcategoryId,
                    documentSystemId: generalInfo.documentSystemId,
                    documentTypeId: generalInfo.documentTypeId,
                    fileExtension: null,
                    fileName: this.files[0].name,
                    publicationDate: generalInfo.publicationDate,
                    Ssn: this.form.get('ssn').value,
                    stafraentIslandId: null,
                    submitToStafraentIsland: generalInfo.submitToStafraentIsland ? generalInfo.submitToStafraentIsland : false,
                    timeStamp: null,
                    title: generalInfo.title,
                    svid: generalInfo.svid,
                    deild: generalInfo.deild,
                    submitToPortalLSR: generalInfo.submitToPortalLSR,
                },
                this.files[0]
            )
            .subscribe((res) => {
                this.formEnabled = true;
                if (res.createDocSuccess) {
                    this.router.navigate(['/skjalasafn/skoda-skjol']);
                    if (generalInfo.submitToStafraentIsland && !res.stafraentIslandSuccess) {
                        this.alertsService.changealertHeader('Villa kom upp');
                        this.alertsService.changeAlertErrorMessage('Ekki tókst að senda skjal í pósthólf Stafræns Íslands');
                        this.alertsService.changeAlertErrorShow(true);
                    }
                }
                // Færa notanda yfir á skoða skjal
            });
    }

    storeSelectedValues() {
        this.localStorageDivisionDefaultValues = {
            documentType: this.generalInfoComponent.form.get('documentTypeId').value,
            documentSystem: this.generalInfoComponent.form.get('documentSystemId').value,
            documentCategory: this.generalInfoComponent.form.get('documentCategoryId').value,
            documentSubcategory: this.generalInfoComponent.form.get('documentSubcategoryId').value,
            division: this.generalInfoComponent.form.get('svid').value,
            department: this.generalInfoComponent.form.get('deild').value,
            defaultValueSelection: this.generalInfoComponent.form.get('defaultValue').value,
        };
        localStorage.setItem('documentsDivisionDefaultValues', JSON.stringify(this.localStorageDivisionDefaultValues));
    }

    recipientSelected(option: ExposedListPublicRegistryPersonsRes | ExposedListPublicRegistryCompaniesRes): void {
        this.selectedSingleDocRecipient = option;

        let ssn: string | undefined;

        if ('ssn' in option) {
            ssn = option.ssn;
        } else if ('idNumber' in option) {
            ssn = option.idNumber;
        }

        this.form.get('ssn')?.setValue(ssn ? ssn.replace(/-/g, '') : null);
    }

    //#endregion

    //#region Helper methods

    formatKennitala(kt): string {
        return formatKennitala(kt);
    }

    formatPersonDetails = formatPersonDetails;

    changeDocumentType(event): void {
        this.files = [];
        if (event.value == 'template') {
            this.form.get('documentTemplateKey').setValidators(Validators.required);
            this.form.get('documentTemplateKey').updateValueAndValidity();
            this.form.get('documentTemplatePaymentFromDate').setValidators(Validators.required);
            this.form.get('documentTemplatePaymentFromDate').updateValueAndValidity();
            this.form.get('documentTemplatePaymentToDate').setValidators(Validators.required);
            this.form.get('documentTemplatePaymentToDate').updateValueAndValidity();
        } else {
            this.form.get('documentTemplateKey').clearValidators();
            this.form.get('documentTemplateKey').updateValueAndValidity();
            this.form.get('documentTemplatePaymentFromDate').clearValidators();
            this.form.get('documentTemplatePaymentFromDate').updateValueAndValidity();
            this.form.get('documentTemplatePaymentToDate').clearValidators();
            this.form.get('documentTemplatePaymentToDate').updateValueAndValidity();
        }
    }

    revertSingleDocAllChanges() {
        if (confirm('Ertu viss um að þú viljir hætta við?')) {
            this.form.markAsPristine();
            this.initForm();
        }
    }

    // Checks if we are in the possition to create the given template
    checkPensionTemplateDocumentReady() {
        if (
            this.form.get('documentType').value == 'template' &&
            this.form.get('ssn').valid &&
            this.form.get('documentTemplateKey').valid &&
            this.form.get('documentTemplatePaymentFromDate').valid &&
            this.form.get('documentTemplatePaymentToDate').valid &&
            this.form.get('documentTemplateSalaryFromDate').valid &&
            this.form.get('documentTemplateSalaryToDate').valid
        ) {
            this.documentPensionTemplateReady = true;
        } else {
            this.documentPensionTemplateReady = false;
        }
    }

    public descriptionSingleDocOnChange({ editor }: ChangeEvent) {
        this.form.markAsDirty();
        this.descriptionSingleDocTextHTML = editor.getData();
        this.form.get('description').setValue(editor.getData());
    }

    //#endregion
}
